// GST math — the money-critical splits for tax invoices.
//
// India GST rules for M5:
//   Intra-state (client PoS state == company state) → CGST + SGST (each = ½ rate)
//   Inter-state (otherwise)                          → IGST (full rate)
//
// Rates are expressed in basis-points-of-percent (bpp) — 100 = 1%. This
// matches @bits/domain/money's `applyRateBpp` and avoids float drift.
//
// Rounding: per-line half-even (banker's) via applyRateBpp. When splitting an
// intra-state rate in half, we compute cgst = applyRateBpp(base, rate/2) and
// sgst = totalGst - cgst — this guarantees cgst+sgst == totalGst regardless
// of rounding for odd rates like 5% (2.5% each).

import { RuleViolationError } from '../errors.js';
import { stateCodeFromPlace } from '../gstin/index.js';
import { paise, applyRateBpp, type Paise } from '../money/index.js';

export interface LineTax {
  cgstPaise: Paise;
  sgstPaise: Paise;
  igstPaise: Paise;
}

const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

/** Return the 2-char state code from a GSTIN. `null` if not well-formed. */
export function stateCodeFromGstin(gstin: string | null | undefined): string | null {
  if (!gstin) return null;
  const s = gstin.trim().toUpperCase();
  if (!GSTIN_RE.test(s)) return null;
  return s.slice(0, 2);
}

/** Whether the place of supply (a state code or a state name) is the company's own state. */
export function isIntraState(placeOfSupplyState: string, companyStateCode: string | null): boolean {
  if (!companyStateCode) return false;
  return stateCodeFromPlace(placeOfSupplyState) === companyStateCode;
}

/**
 * Compute per-line CGST / SGST / IGST for the given taxable base and rate.
 * Intra: split rate in half; SGST = totalGst - CGST to preserve the sum.
 */
export function computeLineTax(taxablePaise: Paise, gstRateBpp: number, intraState: boolean): LineTax {
  if (!Number.isInteger(gstRateBpp) || gstRateBpp < 0 || gstRateBpp > 10_000) {
    throw new RuleViolationError('gst.bad_rate', `rate must be integer 0..10000 bpp, got ${gstRateBpp}`);
  }
  if (gstRateBpp === 0) {
    return { cgstPaise: paise(0), sgstPaise: paise(0), igstPaise: paise(0) };
  }
  if (intraState) {
    const totalGst = applyRateBpp(taxablePaise, gstRateBpp);
    const half = Math.floor(gstRateBpp / 2);
    const cgst = applyRateBpp(taxablePaise, half);
    // sgst absorbs any 1-paise rounding residue so cgst + sgst == totalGst.
    const sgst = paise(totalGst - cgst);
    return { cgstPaise: cgst, sgstPaise: sgst, igstPaise: paise(0) };
  }
  const igst = applyRateBpp(taxablePaise, gstRateBpp);
  return { cgstPaise: paise(0), sgstPaise: paise(0), igstPaise: igst };
}

// ----- Document totals -----

/** Quantity is stored as milli-units — 1000 = 1.000, 1500 = 1.500. */
export type QtyMilli = number & { readonly __brand: 'qty_milli' };

export function qtyMilli(n: number): QtyMilli {
  if (!Number.isInteger(n) || n < 0) {
    throw new RuleViolationError('billing.bad_qty', `qty (milli-units) must be non-negative integer, got ${n}`);
  }
  return n as QtyMilli;
}

export interface LineInput {
  description: string;
  hsnSac?: string | null;
  qty: QtyMilli;
  ratePaise: Paise;
  gstRateBpp: number;
}

export interface LineOutput extends LineInput {
  taxablePaise: Paise;
  cgstPct: number;
  cgstPaise: Paise;
  sgstPct: number;
  sgstPaise: Paise;
  igstPct: number;
  igstPaise: Paise;
  lineTotalPaise: Paise;
}

export interface DocumentTotals {
  lines: LineOutput[];
  subtotalPaise: Paise;
  taxPaise: Paise;
  totalPaise: Paise;
}

/**
 * Compute all lines + document totals. Qty is milli-units; taxable = qty * rate / 1000
 * (rounded half-even). GST split follows `intraState`.
 */
export function computeDocumentTotals(lines: readonly LineInput[], intraState: boolean): DocumentTotals {
  const out: LineOutput[] = [];
  let subtotal = 0n;
  let tax = 0n;
  for (const l of lines) {
    // taxable = qty (milli) * rate / 1000, half-even
    const raw = BigInt(l.qty) * l.ratePaise;
    const q = raw / 1000n;
    const r = raw % 1000n;
    // half-even rounding to nearest paise
    let taxableRaw: bigint;
    const twiceR = r * 2n;
    if (twiceR < 1000n) taxableRaw = q;
    else if (twiceR > 1000n) taxableRaw = q + 1n;
    else taxableRaw = q % 2n === 0n ? q : q + 1n;
    const taxable = paise(taxableRaw);

    const t = computeLineTax(taxable, l.gstRateBpp, intraState);
    const lineTotal = paise(taxable + t.cgstPaise + t.sgstPaise + t.igstPaise);

    const half = Math.floor(l.gstRateBpp / 2);
    const cgstPct = intraState && l.gstRateBpp > 0 ? half : 0;
    const sgstPct = intraState && l.gstRateBpp > 0 ? l.gstRateBpp - half : 0;
    const igstPct = intraState ? 0 : l.gstRateBpp;

    out.push({
      ...l,
      taxablePaise: taxable,
      cgstPct,
      cgstPaise: t.cgstPaise,
      sgstPct,
      sgstPaise: t.sgstPaise,
      igstPct,
      igstPaise: t.igstPaise,
      lineTotalPaise: lineTotal,
    });
    subtotal += taxable;
    tax += t.cgstPaise + t.sgstPaise + t.igstPaise;
  }
  return {
    lines: out,
    subtotalPaise: paise(subtotal),
    taxPaise: paise(tax),
    totalPaise: paise(subtotal + tax),
  };
}

// ----- Document number formatter -----

/**
 * Number templates are effective-dated config (hard rule 10). Tokens:
 * {FY} → 26-27, {FY4} → 2026-27, {FYSTART} → 2026, {FYEND} → 2027,
 * {SEQ} → the per-FY sequence, {SEQ:n} zero-padded to n digits.
 * A template must carry the sequence. When it also carries an FY token the
 * sequence restarts every April; without one it runs on across financial
 * years so that numbers stay unique.
 */
export const DEFAULT_NUMBER_TEMPLATES: Readonly<Record<string, string>> = {
  tax_invoice: 'INV/{FY}/{SEQ:5}',
  bill_of_supply: 'BOS/{FY}/{SEQ:5}',
  credit_note: 'CN/{FY}/{SEQ:5}',
  receipt: 'RCT/{FY}/{SEQ:5}',
};

export const MAX_DOC_NUMBER_LENGTH = 48;
const MAX_SEQ_DIGITS = 8;
const TOKEN_RE = /\{(FY|FY4|FYSTART|FYEND|SEQ(?::(\d+))?)\}/g;
const LITERAL_RE = /^[A-Za-z0-9/._\- ]*$/;
const FY_TOKENS = new Set(['FY', 'FY4', 'FYSTART', 'FYEND']);

export function assertValidNumberTemplate(template: string): void {
  if (template.length === 0 || template.length > MAX_DOC_NUMBER_LENGTH) {
    throw new RuleViolationError(
      'billing.number_template.length',
      `A number format must be 1–${MAX_DOC_NUMBER_LENGTH} characters.`,
      { template },
    );
  }
  let seqCount = 0;
  let seqDigits = 0;
  const literal = template.replace(TOKEN_RE, (_m, name: string, digits?: string) => {
    if (name.startsWith('SEQ')) {
      seqCount += 1;
      seqDigits = digits === undefined ? 1 : Number(digits);
    }
    return '';
  });
  if (/[{}]/.test(literal)) {
    throw new RuleViolationError(
      'billing.number_template.unknown_token',
      'Only {FY}, {FY4}, {FYSTART}, {FYEND}, {SEQ} and {SEQ:n} are allowed in braces.',
      { template },
    );
  }
  if (!LITERAL_RE.test(literal)) {
    throw new RuleViolationError(
      'billing.number_template.bad_characters',
      'Besides tokens, a number format may only contain letters, digits, spaces, "/", "-", "_" and ".".',
      { template },
    );
  }
  if (seqCount !== 1) {
    throw new RuleViolationError(
      'billing.number_template.sequence',
      'A number format must contain exactly one {SEQ} or {SEQ:n} token.',
      { template },
    );
  }
  if (seqDigits < 1 || seqDigits > MAX_SEQ_DIGITS) {
    throw new RuleViolationError(
      'billing.number_template.sequence_width',
      `{SEQ:n} must pad to 1–${MAX_SEQ_DIGITS} digits.`,
      { template },
    );
  }
  if (renderDocNumberUnchecked(template, 2099, 10 ** MAX_SEQ_DIGITS - 1).length > MAX_DOC_NUMBER_LENGTH) {
    throw new RuleViolationError(
      'billing.number_template.too_long',
      `Numbers rendered from this format would exceed ${MAX_DOC_NUMBER_LENGTH} characters.`,
      { template },
    );
  }
}

/** Whether numbers rendered from this template change with the financial year. */
export function templateHasFyToken(template: string): boolean {
  return [...template.matchAll(TOKEN_RE)].some((m) => FY_TOKENS.has(m[1]!));
}

/**
 * First sequence number for a (docType, fy) series that has issued nothing yet.
 * A starting number set on the format wins over the carry-over from the
 * previous year, which only applies to templates without a year token.
 */
export function seedSequence(input: { startAt: number | null; carryOver: number | null }): number {
  return Math.max(1, input.startAt ?? 1, input.carryOver ?? 1);
}

function renderDocNumberUnchecked(template: string, fy: number, seq: number): string {
  const yy1 = String(fy).slice(-2);
  const yy2 = String(fy + 1).slice(-2);
  return template.replace(TOKEN_RE, (_m, name: string, digits?: string) => {
    switch (name) {
      case 'FY':
        return `${yy1}-${yy2}`;
      case 'FY4':
        return `${fy}-${yy2}`;
      case 'FYSTART':
        return String(fy);
      case 'FYEND':
        return String(fy + 1);
      default:
        return digits === undefined ? String(seq) : String(seq).padStart(Number(digits), '0');
    }
  });
}

/** Render a document number from a validated template. `fy` is the FY start year (April–March). */
export function renderDocNumber(template: string, fy: number, seq: number): string {
  assertValidNumberTemplate(template);
  if (!Number.isInteger(seq) || seq < 1) {
    throw new RuleViolationError('billing.bad_sequence', `sequence must be positive integer, got ${seq}`);
  }
  return renderDocNumberUnchecked(template, fy, seq);
}

/**
 * Read the sequence back out of a number written under `template` for `fy`.
 * Returns null when the number does not follow the template exactly.
 */
export function parseDocNumber(template: string, fy: number, number: string): number | null {
  assertValidNumberTemplate(template);
  const yy1 = String(fy).slice(-2);
  const yy2 = String(fy + 1).slice(-2);
  const escape = (s: string): string => s.replace(/[.*+?^${}()|[\]\\/-]/g, '\\$&');
  let pattern = '^';
  let last = 0;
  for (const m of template.matchAll(TOKEN_RE)) {
    pattern += escape(template.slice(last, m.index));
    last = m.index + m[0].length;
    switch (m[1]) {
      case 'FY':
        pattern += escape(`${yy1}-${yy2}`);
        break;
      case 'FY4':
        pattern += escape(`${fy}-${yy2}`);
        break;
      case 'FYSTART':
        pattern += escape(String(fy));
        break;
      case 'FYEND':
        pattern += escape(String(fy + 1));
        break;
      default:
        pattern += m[2] === undefined ? '(\\d{1,8})' : `(\\d{${m[2]},8})`;
    }
  }
  pattern += escape(template.slice(last)) + '$';
  const hit = new RegExp(pattern).exec(number);
  if (!hit) return null;
  const seq = Number(hit[1]);
  if (!Number.isInteger(seq) || seq < 1) return null;
  if (renderDocNumberUnchecked(template, fy, seq) !== number) return null;
  return seq;
}

/** Format with the doc type's default template unless an effective one is supplied. */
export function formatDocNumber(docType: string, fy: number, seq: number, template?: string): string {
  const effective = template ?? DEFAULT_NUMBER_TEMPLATES[docType];
  if (!effective) {
    throw new RuleViolationError('billing.unknown_doc_type', `unknown doc type "${docType}"`);
  }
  return renderDocNumber(effective, fy, seq);
}

// GST state codes → state abbreviations, per the GSTN master list. PT is a
// state levy, so the payroll engine keys its slab lookup on this.
const GST_STATE_ABBR: Record<string, string> = {
  '01': 'JK', '02': 'HP', '03': 'PB', '04': 'CH', '05': 'UK', '06': 'HR', '07': 'DL',
  '08': 'RJ', '09': 'UP', '10': 'BR', '11': 'SK', '12': 'AR', '13': 'NL', '14': 'MN',
  '15': 'MZ', '16': 'TR', '17': 'ML', '18': 'AS', '19': 'WB', '20': 'JH', '21': 'OR',
  '22': 'CG', '23': 'MP', '24': 'GJ', '26': 'DD', '27': 'MH', '28': 'AP', '29': 'KA',
  '30': 'GA', '31': 'LD', '32': 'KL', '33': 'TN', '34': 'PY', '35': 'AN', '36': 'TS',
  '37': 'AD', '38': 'LA',
};

export function stateAbbrFromGstin(gstin: string | null | undefined): string | null {
  const code = stateCodeFromGstin(gstin);
  return code ? (GST_STATE_ABBR[code] ?? null) : null;
}
